import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Zap, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CriticalPayCard (admin) — grant/revoke a talent's Critical Pay designation.
 * Applies only to that talent's FUTURE eligible bookings, writes an audit row,
 * and drives the expedited 7-business-day payout queue in accounting.
 */
const CriticalPayCard = ({ talentId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [history, setHistory] = useState([]);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/payout-terms/talent/${talentId}/critical-pay`);
      setEnabled(!!res.data.critical_pay_enabled);
      setHistory(res.data.history || []);
    } catch {
      /* keep card usable */
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/payout-terms/talent/${talentId}/critical-pay`, {
        enabled: next,
        reason: reason.trim() || null,
      });
      setEnabled(res.data.critical_pay_enabled);
      setReason('');
      toast.success(
        next
          ? `Critical Pay granted — applied to ${res.data.affected_future_bookings} upcoming booking(s).`
          : `Critical Pay removed — cleared on ${res.data.affected_future_bookings} upcoming booking(s).`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update Critical Pay');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="critical-pay-card" className={enabled ? 'border-amber-300 bg-amber-50/40' : ''}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className={`w-5 h-5 ${enabled ? 'text-amber-500' : 'text-slate-400'}`} />
          Critical Pay
          {enabled && (
            <Badge className="bg-amber-500 text-white border-0" data-testid="critical-pay-status-badge">ACTIVE</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              When on, this talent's eligible earnings follow the expedited Critical Pay
              process — targeted within <span className="font-semibold">7 business days</span> of
              timesheet approval. Applies to future bookings only; completed bookings are unchanged.
            </p>

            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional — recorded in the audit trail)"
              className="text-sm"
              rows={2}
              data-testid="critical-pay-reason-input"
            />

            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-sm font-medium text-slate-800">
                {enabled ? 'Critical Pay is ON for this talent' : 'Critical Pay is OFF'}
              </div>
              <div className="flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
                <Switch
                  checked={enabled}
                  disabled={saving}
                  onCheckedChange={toggle}
                  data-testid="critical-pay-toggle"
                />
              </div>
            </div>

            {history.length > 0 && (
              <div className="pt-1" data-testid="critical-pay-history">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-2">
                  <History className="w-3.5 h-3.5" /> Audit history
                </div>
                <div className="space-y-1.5">
                  {history.map((h, i) => (
                    <div key={i} className="text-xs text-slate-600 flex items-start gap-2" data-testid={`critical-pay-history-row-${i}`}>
                      <Badge variant="outline" className={h.action === 'granted' ? 'text-emerald-700 border-emerald-300' : 'text-rose-700 border-rose-300'}>
                        {h.action}
                      </Badge>
                      <span className="flex-1">
                        by {h.admin_name || 'admin'} · {new Date(h.created_at).toLocaleDateString()}
                        {h.affected_future_bookings ? ` · ${h.affected_future_bookings} booking(s)` : ''}
                        {h.reason ? ` · "${h.reason}"` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CriticalPayCard;
